import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, ArrowRight, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { calculateFocalAreaCrop, clampCropValues } from '@/lib/focal-area-utils';

// Types matching the annotation_coordinates structure
interface AnnotationCoordinate {
  x: number;
  y: number;
}

interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
}

// Internal viewport state that includes container dimensions for offset calculation
interface ViewportStateInternal extends ViewportState {
  containerWidth: number;
  containerHeight: number;
}

interface Annotation {
  num: number;
  label: string;
  parent_label?: string;
  image?: string;
  before: AnnotationCoordinate | null;
  after: AnnotationCoordinate | null;
  marker_before?: AnnotationCoordinate;  // Explicit marker position (overrides before)
  marker_after?: AnnotationCoordinate;   // Explicit marker position (overrides after)
  viewport_before?: ViewportState;
  viewport_after?: ViewportState;
  hideMarker1?: boolean;  // Hide marker on before/primary image
  hideMarker2?: boolean;  // Hide marker on after/secondary image
}

interface AnnotationCoordinates {
  cloudinary_before?: string;
  cloudinary_after?: string;
  annotations: Annotation[];
}

interface AnnotationEditorProps {
  annotationCoordinates: AnnotationCoordinates;
  initialAnnotationCoordinates?: AnnotationCoordinates; // Original state for reset
  primaryOriginalUrl: string | null;
  secondaryOriginalUrl: string | null;
  onChange: (coordinates: AnnotationCoordinates) => void;
  vBefore?: string;
  vAfter?: string;
}

// CroppedAnnotationImage - handles a single image with pan/zoom
interface CroppedAnnotationImageProps {
  imageUrl: string;
  cropCenterX: number; // Original annotation coordinate for crop centering (stable)
  cropCenterY: number; // Original annotation coordinate for crop centering (stable)
  markerX: number; // Explicit marker position for rendering (can change during drag)
  markerY: number; // Explicit marker position for rendering (can change during drag)
  side: 'before' | 'after';
  num: number;
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onMinZoomCalculated?: (minZoom: number) => void;
  showMarker: boolean;
  hasExplicitMarker: boolean; // True if marker_before/marker_after is set
  onMarkerDrag?: (x: number, y: number) => void;
  initialViewport?: ViewportState;
  onViewportChange?: (viewport: ViewportStateInternal) => void;
  onInteractionEnd?: () => void; // Called when pan/zoom interaction ends
  // Add marker mode
  isAddMode?: boolean;
  onAddMarker?: (x: number, y: number) => void;
  canAddMarker?: boolean;
  onEnterAddMode?: () => void;
  // Hide marker control
  hideMarker?: boolean;
  onHideMarkerChange?: (hide: boolean) => void;
}

// BASE_SCALE matches the frontend's base scaling factor
const BASE_SCALE = 0.75;

function CroppedAnnotationImage({
  imageUrl,
  cropCenterX, // Original annotation coordinate for crop centering (stable)
  cropCenterY, // Original annotation coordinate for crop centering (stable)
  markerX, // Explicit marker position for rendering (can change during drag)
  markerY, // Explicit marker position for rendering (can change during drag)
  side,
  num,
  zoomLevel,
  onZoomChange,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onMinZoomCalculated,
  showMarker,
  hasExplicitMarker,
  onMarkerDrag,
  initialViewport,
  onViewportChange,
  onInteractionEnd,
  isAddMode,
  onAddMarker,
  canAddMarker,
  onEnterAddMode,
  hideMarker,
  onHideMarkerChange,
}: CroppedAnnotationImageProps) {
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  // panOffset represents how much the user has panned from the centered position
  // Matches frontend: positive values = panned right/down from center
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isMarkerDragging, setIsMarkerDragging] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const [initialized, setInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const onZoomChangeRef = useRef(onZoomChange);
  const lastViewportRef = useRef<ViewportState | null>(null);
  const lastPanOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Keep ref up to date
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  }, [onZoomChange]);

  // Measure container size
  useEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };
    measureContainer();
    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, []);

  // Load image to get dimensions
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Calculate minimum zoom (matching frontend formula)
  useEffect(() => {
    if (imageSize && containerSize.width > 0 && onMinZoomCalculated) {
      const fitZoomX = containerSize.width / (imageSize.width * BASE_SCALE);
      const fitZoomY = containerSize.height / (imageSize.height * BASE_SCALE);
      const fitZoom = Math.min(fitZoomX, fitZoomY);
      const minZoom = Math.max(0.05, fitZoom);
      onMinZoomCalculated(minZoom);
    }
  }, [imageSize, containerSize, onMinZoomCalculated]);

  // Initialize from saved viewport (runs once when image loads)
  useEffect(() => {
    if (imageSize && containerSize.width > 0 && !initialized) {
      const targetZoom = initialViewport?.zoom ?? DEFAULT_ZOOM;

      if (targetZoom !== zoomLevel) {
        onZoomChangeRef.current(targetZoom);
      }

      // Check if we have a valid saved viewport (not just undefined, but also not zero/empty)
      const hasValidViewport = initialViewport &&
        initialViewport.panX !== undefined &&
        initialViewport.panY !== undefined &&
        (initialViewport.panX !== 0 || initialViewport.panY !== 0);

      // Apply saved viewport pan offsets
      if (hasValidViewport) {
        // CRITICAL: Backend calculated viewport positions based on OLD viewport dimensions (400×300)
        // but we're now using NEW dimensions (800×600). We need to convert.
        // Backend formula: panX = marker.x - OLD_viewportWidth/2
        // New formula: panX = marker.x - NEW_viewportWidth/2
        // Adjustment: newPanX = oldPanX + (OLD_width/2 - NEW_width/2)
        const OLD_VIEWPORT_WIDTH = 400;
        const OLD_VIEWPORT_HEIGHT = 300;
        const adjustedPanX = initialViewport.panX + (OLD_VIEWPORT_WIDTH / 2 - containerSize.width / 2);
        const adjustedPanY = initialViewport.panY + (OLD_VIEWPORT_HEIGHT / 2 - containerSize.height / 2);

        // Backend sends panX/panY as CROP POSITIONS in original coordinates
        // Formula: panX = marker.x - viewportWidth/2 (e.g., 90 - 400 = -310)
        // Convert to offsets from centered position at current scale and viewport size
        const scale = BASE_SCALE * zoomLevel;
        const scaledMarkerX = cropCenterX * scale;
        const scaledMarkerY = cropCenterY * scale;

        // Where would the crop be if we centered the marker?
        const centeredCropX = Math.max(0, scaledMarkerX - containerSize.width / 2);
        const centeredCropY = Math.max(0, scaledMarkerY - containerSize.height / 2);

        // Where does the backend want the crop (using adjusted coordinates)?
        const savedCropX = adjustedPanX * scale;
        const savedCropY = adjustedPanY * scale;

        // Offset = centered - saved (opposite sign!)
        const offsetX = centeredCropX - savedCropX;
        const offsetY = centeredCropY - savedCropY;

        lastViewportRef.current = { ...initialViewport };
        lastPanOffsetRef.current = { x: offsetX, y: offsetY };
        setPanOffset({
          x: offsetX,
          y: offsetY,
        });
      } else {
        // Default: center viewport on the marker (no offset from centered position)
        lastViewportRef.current = { zoom: DEFAULT_ZOOM, panX: 0, panY: 0 };
        lastPanOffsetRef.current = { x: 0, y: 0 };
        setPanOffset({ x: 0, y: 0 });
      }

      setInitialized(true);
    }
  }, [imageSize, containerSize, initialized, initialViewport, zoomLevel, cropCenterX, cropCenterY, side]);

  // Update lastPanOffsetRef when marker position changes (but keep same viewport)
  useEffect(() => {
    if (initialized && initialViewport && imageSize && containerSize.width > 0) {
      // Recalculate what the offset should be based on current marker position
      const scale = BASE_SCALE * zoomLevel;
      const scaledMarkerX = cropCenterX * scale;
      const scaledMarkerY = cropCenterY * scale;

      const centeredCropX = Math.max(0, scaledMarkerX - containerSize.width / 2);
      const centeredCropY = Math.max(0, scaledMarkerY - containerSize.height / 2);

      const savedCropX = initialViewport.panX * scale;
      const savedCropY = initialViewport.panY * scale;

      const expectedOffsetX = centeredCropX - savedCropX;
      const expectedOffsetY = centeredCropY - savedCropY;

      // Update the ref to match what offset should be for this marker position
      // This prevents jumps when the viewport prop hasn't actually changed
      lastPanOffsetRef.current = { x: expectedOffsetX, y: expectedOffsetY };
    }
  }, [cropCenterX, cropCenterY, initialized, initialViewport, imageSize, containerSize, zoomLevel]);

  // Update viewport when initialViewport changes (for hide/unhide marker functionality)
  // NOTE: This should ONLY run when initialViewport semantically changes
  useEffect(() => {
    if (initialized && initialViewport) {
      // Check if viewport has actually changed
      const hasChanged =
        !lastViewportRef.current ||
        lastViewportRef.current.zoom !== initialViewport.zoom ||
        lastViewportRef.current.panX !== initialViewport.panX ||
        lastViewportRef.current.panY !== initialViewport.panY;

      if (hasChanged) {
        lastViewportRef.current = { ...initialViewport };

        const targetZoom = initialViewport.zoom ?? DEFAULT_ZOOM;
        if (targetZoom !== zoomLevel) {
          onZoomChangeRef.current(targetZoom);
        }

        if (initialViewport.panX !== undefined && initialViewport.panY !== undefined && imageSize && containerSize.width > 0) {
          // Convert crop positions to offsets (same as initialization)
          const scale = BASE_SCALE * zoomLevel;
          const scaledMarkerX = cropCenterX * scale;
          const scaledMarkerY = cropCenterY * scale;

          const centeredCropX = Math.max(0, scaledMarkerX - containerSize.width / 2);
          const centeredCropY = Math.max(0, scaledMarkerY - containerSize.height / 2);

          const savedCropX = initialViewport.panX * scale;
          const savedCropY = initialViewport.panY * scale;

          const offsetX = centeredCropX - savedCropX;
          const offsetY = centeredCropY - savedCropY;

          // Only update if the change is significant (more than 1 pixel from our last set value)
          // This prevents jumps from rounding errors during propagate/reload cycles
          const deltaX = Math.abs(offsetX - lastPanOffsetRef.current.x);
          const deltaY = Math.abs(offsetY - lastPanOffsetRef.current.y);
          const isSignificantChange = deltaX > 1 || deltaY > 1;

          if (isSignificantChange) {
            lastPanOffsetRef.current = { x: offsetX, y: offsetY };
            setPanOffset({
              x: offsetX,
              y: offsetY,
            });
          }
        }
      }
    }
  }, [initialized, initialViewport?.zoom, initialViewport?.panX, initialViewport?.panY, side, zoomLevel, imageSize, containerSize, cropCenterX, cropCenterY]);

  const isReady = imageSize && containerSize.width > 0 && initialized;

  // Calculate scaled dimensions using BASE_SCALE * zoomLevel (matching frontend)
  const scale = BASE_SCALE * zoomLevel;
  const scaledWidth = imageSize ? imageSize.width * scale : 0;
  const scaledHeight = imageSize ? imageSize.height * scale : 0;
  const viewWidth = containerSize.width || 800;
  const viewHeight = containerSize.height || 600;

  // Calculate crop position (matching frontend exactly)
  // Uses ORIGINAL annotation coordinates (cropCenterX/Y) to keep image stable
  // Step 1: Scale the original annotation point for crop centering
  const scaledCropCenterX = cropCenterX * scale;
  const scaledCropCenterY = cropCenterY * scale;

  // Step 2: Base crop centers the original annotation in viewport
  const baseCropX = Math.max(0, scaledCropCenterX - viewWidth / 2);
  const baseCropY = Math.max(0, scaledCropCenterY - viewHeight / 2);

  // Step 3: Apply pan offset
  let cropX = baseCropX - panOffset.x;
  let cropY = baseCropY - panOffset.y;

  // Step 4: Clamp to image bounds
  const maxScrollX = Math.max(0, scaledWidth - viewWidth);
  const maxScrollY = Math.max(0, scaledHeight - viewHeight);
  cropX = Math.max(0, Math.min(cropX, maxScrollX));
  cropY = Math.max(0, Math.min(cropY, maxScrollY));

  // Calculate marker position in viewport space
  // Uses explicit markerX/Y which can change during drag
  const scaledMarkerX = markerX * scale;
  const scaledMarkerY = markerY * scale;
  const markerViewportX = scaledMarkerX - cropX;
  const markerViewportY = scaledMarkerY - cropY;

  // Check if marker is visible in viewport
  const isMarkerVisible =
    markerViewportX >= -20 && markerViewportX <= viewWidth + 20 &&
    markerViewportY >= -20 && markerViewportY <= viewHeight + 20;

  // Pan handlers (matching frontend logic)
  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start panning if clicking on marker or already dragging marker
    if ((e.target as HTMLElement).closest('.annotation-marker') || isMarkerDragging) {
      return;
    }

    // Handle add mode click - place marker at click position
    if (isAddMode && onAddMarker && imageSize) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportX = e.clientX - rect.left;
      const viewportY = e.clientY - rect.top;

      // Convert viewport position to original image pixel coordinates
      // Reverse the crop/pan/zoom transforms
      const imageX = (viewportX + cropX) / scale;
      const imageY = (viewportY + cropY) / scale;

      // Clamp to image bounds
      const clampedX = Math.max(0, Math.min(imageX, imageSize.width));
      const clampedY = Math.max(0, Math.min(imageY, imageSize.height));

      onAddMarker(clampedX, clampedY);
      return;
    }

    setIsPanning(true);
    setPanStart({
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset.x,
      offsetY: panOffset.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Marker dragging takes priority over panning
    if (isMarkerDragging && onMarkerDrag && imageSize) {
      // Calculate new marker position from mouse position
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Convert viewport position to original image pixel coordinates
      const imagePixelX = (mouseX + cropX) / scale;
      const imagePixelY = (mouseY + cropY) / scale;

      // Clamp to image bounds
      const clampedX = Math.max(0, Math.min(imagePixelX, imageSize.width));
      const clampedY = Math.max(0, Math.min(imagePixelY, imageSize.height));

      onMarkerDrag(clampedX, clampedY);
      return;
    }

    // Don't pan while marker is being dragged
    if (!isPanning || !imageSize || isMarkerDragging) return;

    // Calculate how far mouse moved
    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;

    // Calculate pan bounds (matching frontend)
    const minPanX = baseCropX - maxScrollX;
    const maxPanX = baseCropX;
    const minPanY = baseCropY - maxScrollY;
    const maxPanY = baseCropY;

    // New offset = start offset + delta, clamped to bounds
    const newOffsetX = Math.max(minPanX, Math.min(maxPanX, panStart.offsetX + dx));
    const newOffsetY = Math.max(minPanY, Math.min(maxPanY, panStart.offsetY + dy));

    lastPanOffsetRef.current = { x: newOffsetX, y: newOffsetY };
    setPanOffset({ x: newOffsetX, y: newOffsetY });

    // Notify parent of viewport change during panning
    if (onViewportChange && containerSize.width > 0) {
      onViewportChange({
        zoom: zoomLevel,
        panX: newOffsetX,
        panY: newOffsetY,
        containerWidth: containerSize.width,
        containerHeight: containerSize.height,
      });
    }
  };

  const handleMouseUp = () => {
    const wasPanning = isPanning;
    setIsPanning(false);
    setIsMarkerDragging(false);
    // Notify parent when panning ends
    if (wasPanning && onInteractionEnd) {
      onInteractionEnd();
    }
  };

  const handleMouseLeave = () => {
    const wasPanning = isPanning;
    setIsPanning(false);
    setIsMarkerDragging(false);
    // Notify parent when panning ends
    if (wasPanning && onInteractionEnd) {
      onInteractionEnd();
    }
  };

  const handleMarkerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMarkerDrag) {
      setIsMarkerDragging(true);
    }
  };

  // Report viewport changes to parent
  useEffect(() => {
    if (onViewportChange && containerSize.width > 0 && initialized) {
      onViewportChange({
        zoom: zoomLevel,
        panX: panOffset.x,
        panY: panOffset.y,
        containerWidth: containerSize.width,
        containerHeight: containerSize.height,
      });
    }
  }, [panOffset, zoomLevel, containerSize, onViewportChange, initialized]);

  return (
    <div
      ref={containerRef}
      className={`relative w-[800px] h-[600px] overflow-hidden rounded-lg border bg-gray-100 ${
        isAddMode
          ? 'cursor-crosshair border-2'
          : 'cursor-grab active:cursor-grabbing'
      }`}
      style={isAddMode ? { borderColor: '#aebb36' } : undefined}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {!isReady ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading...</div>
        </div>
      ) : (
        <>
          {/* Zoom controls */}
          <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-white/80 rounded-md p-1">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onZoomOut} title="Zoom out">
              <ZoomOut className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onZoomIn} title="Zoom in">
              <ZoomIn className="h-3 w-3" />
            </Button>
            {onHideMarkerChange && (
              <>
                <div className="h-4 w-px bg-gray-300 mx-0.5" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => onHideMarkerChange(!hideMarker)}
                  title={hideMarker ? "Show marker" : "Hide marker"}
                >
                  {hideMarker ? (
                    <EyeOff className="h-3 w-3 text-gray-500" />
                  ) : (
                    <Eye className="h-3 w-3 text-gray-700" />
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Zoom indicator */}
          <div className="absolute top-2 left-2 z-20 bg-black/50 text-white text-xs px-2 py-1 rounded">
            {Math.round(zoomLevel * 100)}%
          </div>

          {/* Add marker button - only shown when this side can add a marker */}
          {canAddMarker && onEnterAddMode && (
            <Button
              variant={isAddMode ? "default" : "outline"}
              size="sm"
              className={`absolute bottom-2 left-2 z-20 h-7 text-xs ${
                isAddMode
                  ? 'text-white border-0'
                  : 'bg-white/90 hover:bg-white'
              }`}
              style={isAddMode ? { backgroundColor: '#aebb36' } : { borderColor: '#aebb36', color: '#7a8a1a' }}
              onClick={onEnterAddMode}
              title={isAddMode ? "Click on image to place marker" : "Add marker to this image"}
            >
              <Plus className="h-3 w-3 mr-1" />
              {isAddMode ? 'Click to place' : 'Add'}
            </Button>
          )}

          {/* Add mode instruction overlay */}
          {isAddMode && (
            <div
              className="absolute bottom-2 right-2 z-20 text-white text-xs px-2 py-1 rounded shadow-lg"
              style={{ backgroundColor: '#aebb36' }}
            >
              Click anywhere to place marker
            </div>
          )}

          {/* Image container - transform moves image to show correct region */}
          <div
            className="absolute select-none z-0"
            style={{
              width: `${scaledWidth}px`,
              height: `${scaledHeight}px`,
              transform: `translate(${-cropX}px, ${-cropY}px)`,
            }}
          >
            <img
              src={imageUrl}
              alt={`${side} screenshot`}
              crossOrigin="anonymous"
              className="select-none pointer-events-none block w-full h-full"
              draggable={false}
            />
          </div>

          {/* Marker indicator - positioned based on whether explicit marker exists */}
          {showMarker && isMarkerVisible && (
            hasExplicitMarker ? (
              // Explicit marker: positioned at actual coordinates, draggable
              <div
                className={`annotation-marker absolute flex items-center z-10 ${
                  onMarkerDrag ? 'cursor-move' : ''
                }`}
                style={{
                  left: Math.max(0, Math.min(viewWidth - 40, markerViewportX - 20)),
                  top: Math.max(8, Math.min(viewHeight - 32, markerViewportY - 12)),
                }}
                onMouseDown={handleMarkerMouseDown}
              >
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-white shadow-lg ${isMarkerDragging ? 'ring-2 ring-blue-400 scale-110' : ''}`}
                  style={{ backgroundColor: '#aebb36' }}
                >
                  <span>{num}</span>
                  <span className="text-[10px] opacity-75">→</span>
                </div>
              </div>
            ) : (
              // No explicit marker: fixed to left edge, tracks Y position only
              <div
                className={`annotation-marker absolute left-2 flex items-center z-10 ${
                  onMarkerDrag ? 'cursor-move' : ''
                }`}
                style={{
                  top: Math.max(8, Math.min(viewHeight - 32, markerViewportY - 12)),
                }}
                onMouseDown={handleMarkerMouseDown}
              >
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-white shadow-lg ${isMarkerDragging ? 'ring-2 ring-blue-400 scale-110' : ''}`}
                  style={{ backgroundColor: '#aebb36' }}
                >
                  <span>{num}</span>
                  <span className="text-[10px] opacity-75">→</span>
                </div>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

// AnnotationPair - handles before/after for a single annotation
interface AnnotationPairEditorProps {
  annotation: Annotation;
  annotationIndex: number;
  beforeImageUrl: string;
  afterImageUrl: string;
  beforeImageSize: { width: number; height: number } | null;
  afterImageSize: { width: number; height: number } | null;
  onAnnotationChange: (index: number, annotation: Annotation) => void;
  onAnnotationDelete: (index: number) => void;
  initialAnnotation: Annotation; // Original state for reset
  vBefore?: string;
  vAfter?: string;
}

const DEFAULT_ZOOM = 1.0; // 100% zoom by default

function AnnotationPairEditor({
  annotation,
  annotationIndex,
  beforeImageUrl,
  afterImageUrl,
  beforeImageSize,
  afterImageSize,
  onAnnotationChange,
  onAnnotationDelete,
  initialAnnotation,
  vBefore,
  vAfter,
}: AnnotationPairEditorProps) {
  // SHARED zoom level for both before and after images
  // Initialize from saved viewport (prefer before, fallback to after, default to 1)
  const initialZoom = annotation.viewport_before?.zoom ?? annotation.viewport_after?.zoom ?? DEFAULT_ZOOM;

  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const [minZoom, setMinZoom] = useState(0.1);

  // Add marker mode state
  const [addModeImage, setAddModeImage] = useState<'before' | 'after' | null>(null);

  // Sync zoom level when annotation viewport changes (e.g., after reload)
  useEffect(() => {
    const newZoom = annotation.viewport_before?.zoom ?? annotation.viewport_after?.zoom ?? DEFAULT_ZOOM;
    if (newZoom !== zoomLevel) {
      setZoomLevel(newZoom);
    }
  }, [annotation.viewport_before?.zoom, annotation.viewport_after?.zoom]);

  // Track current viewport states (updated by CroppedAnnotationImage via refs)
  const beforeViewportRef = useRef<ViewportStateInternal | null>(null);
  const afterViewportRef = useRef<ViewportStateInternal | null>(null);

  const hasBefore = annotation.before !== null;
  const hasAfter = annotation.after !== null;

  // Determine if we can add a marker to each side
  // Only show add button when one side has marker but this side doesn't
  const canAddBefore = hasAfter && !hasBefore;
  const canAddAfter = hasBefore && !hasAfter;

  // ORIGINAL coordinates for crop centering (keeps image stable during marker drag)
  // Fallback: before → after → 0
  const cropBeforeX = annotation.before?.x ?? annotation.after?.x ?? 0;
  const cropBeforeY = annotation.before?.y ?? annotation.after?.y ?? 0;
  const cropAfterX = annotation.after?.x ?? annotation.before?.x ?? 0;
  const cropAfterY = annotation.after?.y ?? annotation.before?.y ?? 0;

  // MARKER position for rendering (uses explicit position if set, otherwise original)
  // Fallback: marker_before → before → after → 0
  const markerBeforeX = annotation.marker_before?.x ?? annotation.before?.x ?? annotation.after?.x ?? 0;
  const markerBeforeY = annotation.marker_before?.y ?? annotation.before?.y ?? annotation.after?.y ?? 0;
  const markerAfterX = annotation.marker_after?.x ?? annotation.after?.x ?? annotation.before?.x ?? 0;
  const markerAfterY = annotation.marker_after?.y ?? annotation.after?.y ?? annotation.before?.y ?? 0;

  // Track if zoom was changed by user interaction (not from prop update)
  const zoomChangedByUserRef = useRef(false);

  // Shared zoom handlers (affects both images)
  const handleZoomIn = useCallback(() => {
    zoomChangedByUserRef.current = true;
    setZoomLevel(prev => {
      const newZoom = Math.min(prev + 0.25, 3);
      const zoomRatio = newZoom / prev;

      // Scale pan offsets proportionally to keep the viewport center at the same image location
      if (beforeViewportRef.current) {
        beforeViewportRef.current.panX *= zoomRatio;
        beforeViewportRef.current.panY *= zoomRatio;
      }

      if (afterViewportRef.current) {
        afterViewportRef.current.panX *= zoomRatio;
        afterViewportRef.current.panY *= zoomRatio;
      }

      return newZoom;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    zoomChangedByUserRef.current = true;
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, minZoom);
      const zoomRatio = newZoom / prev;

      // Scale pan offsets proportionally to keep the viewport center at the same image location
      if (beforeViewportRef.current) {
        beforeViewportRef.current.panX *= zoomRatio;
        beforeViewportRef.current.panY *= zoomRatio;
      }

      if (afterViewportRef.current) {
        afterViewportRef.current.panX *= zoomRatio;
        afterViewportRef.current.panY *= zoomRatio;
      }

      return newZoom;
    });
  }, [minZoom]);

  const handleZoomReset = useCallback(() => {
    zoomChangedByUserRef.current = true;
    setZoomLevel(DEFAULT_ZOOM);
  }, []);

  // Propagate both viewports when zoom changes (only from user interaction)
  useEffect(() => {
    if (!zoomChangedByUserRef.current) return;
    zoomChangedByUserRef.current = false;

    if (!beforeViewportRef.current && !afterViewportRef.current) return;

    const scale = BASE_SCALE * zoomLevel;

    const beforeMarkerX = annotation.marker_before?.x ?? annotation.before?.x ?? 0;
    const beforeMarkerY = annotation.marker_before?.y ?? annotation.before?.y ?? 0;
    const afterMarkerX = annotation.marker_after?.x ?? annotation.after?.x ?? 0;
    const afterMarkerY = annotation.marker_after?.y ?? annotation.after?.y ?? 0;

    // Helper to convert offset to crop position in original coordinates
    const convertToCropPosition = (markerX: number, markerY: number, viewport: ViewportStateInternal) => {
      const scaledMarkerX = markerX * scale;
      const scaledMarkerY = markerY * scale;

      const centeredCropX = Math.max(0, scaledMarkerX - viewport.containerWidth / 2);
      const centeredCropY = Math.max(0, scaledMarkerY - viewport.containerHeight / 2);

      const actualCropX = centeredCropX - viewport.panX;
      const actualCropY = centeredCropY - viewport.panY;

      const frontendPanX = Math.round(actualCropX / scale);
      const frontendPanY = Math.round(actualCropY / scale);

      // CRITICAL: Convert back to backend's expected coordinate system (400×300 viewport)
      // Backend expects: panX = marker.x - 200, panY = marker.y - 150
      // Frontend calculates: panX = marker.x - 400, panY = marker.y - 300
      // Adjustment: backendPanX = frontendPanX + 200, backendPanY = frontendPanY + 150
      const OLD_VIEWPORT_WIDTH = 400;
      const OLD_VIEWPORT_HEIGHT = 300;
      const backendPanX = frontendPanX + (viewport.containerWidth / 2 - OLD_VIEWPORT_WIDTH / 2);
      const backendPanY = frontendPanY + (viewport.containerHeight / 2 - OLD_VIEWPORT_HEIGHT / 2);

      return {
        panX: Math.round(backendPanX),
        panY: Math.round(backendPanY),
      };
    };

    const updated = {
      ...annotation,
      viewport_before: beforeViewportRef.current ? {
        zoom: zoomLevel,
        ...convertToCropPosition(beforeMarkerX, beforeMarkerY, beforeViewportRef.current),
      } : annotation.viewport_before,
      viewport_after: afterViewportRef.current ? {
        zoom: zoomLevel,
        ...convertToCropPosition(afterMarkerX, afterMarkerY, afterViewportRef.current),
      } : annotation.viewport_after,
    };
    onAnnotationChange(annotationIndex, updated);
  }, [zoomLevel, annotation, annotationIndex, onAnnotationChange]);

  const handleMinZoomCalculated = (calculatedMinZoom: number) => {
    setMinZoom(calculatedMinZoom);
  };

  // Track viewport changes for before image (stored in ref, propagated on mouse up)
  const handleBeforeViewportChange = useCallback((viewport: ViewportStateInternal) => {
    beforeViewportRef.current = viewport;
  }, []);

  // Track viewport changes for after image (stored in ref, propagated on mouse up)
  const handleAfterViewportChange = useCallback((viewport: ViewportStateInternal) => {
    afterViewportRef.current = viewport;
  }, []);

  // Propagate before viewport to parent (called when interaction ends)
  const propagateBeforeViewport = useCallback(() => {
    if (!beforeViewportRef.current) return;
    const viewport = beforeViewportRef.current;

    // Convert offset back to crop position in ORIGINAL coordinates
    // viewport.panX is an offset from centered position
    // We need to calculate the actual crop position and convert back to original scale
    const beforeMarkerX = annotation.marker_before?.x ?? annotation.before?.x ?? 0;
    const beforeMarkerY = annotation.marker_before?.y ?? annotation.before?.y ?? 0;

    const scale = BASE_SCALE * zoomLevel;
    const scaledMarkerX = beforeMarkerX * scale;
    const scaledMarkerY = beforeMarkerY * scale;

    // Centered crop position
    const centeredCropX = Math.max(0, scaledMarkerX - viewport.containerWidth / 2);
    const centeredCropY = Math.max(0, scaledMarkerY - viewport.containerHeight / 2);

    // Actual crop position = centered - offset
    const actualCropX = centeredCropX - viewport.panX;
    const actualCropY = centeredCropY - viewport.panY;

    // Convert back to original coordinates
    const frontendPanX = actualCropX / scale;
    const frontendPanY = actualCropY / scale;

    // CRITICAL: Convert to backend's coordinate system (400×300 viewport)
    const OLD_VIEWPORT_WIDTH = 400;
    const OLD_VIEWPORT_HEIGHT = 300;
    const backendPanX = frontendPanX + (viewport.containerWidth / 2 - OLD_VIEWPORT_WIDTH / 2);
    const backendPanY = frontendPanY + (viewport.containerHeight / 2 - OLD_VIEWPORT_HEIGHT / 2);

    const updated = {
      ...annotation,
      viewport_before: {
        zoom: zoomLevel,
        panX: Math.round(backendPanX),
        panY: Math.round(backendPanY),
      },
    };
    onAnnotationChange(annotationIndex, updated);
  }, [annotation, annotationIndex, onAnnotationChange, zoomLevel]);

  // Propagate after viewport to parent (called when interaction ends)
  const propagateAfterViewport = useCallback(() => {
    if (!afterViewportRef.current) return;
    const viewport = afterViewportRef.current;

    // Convert offset back to crop position in ORIGINAL coordinates
    // viewport.panX is an offset from centered position
    // We need to calculate the actual crop position and convert back to original scale
    const afterMarkerX = annotation.marker_after?.x ?? annotation.after?.x ?? 0;
    const afterMarkerY = annotation.marker_after?.y ?? annotation.after?.y ?? 0;

    const scale = BASE_SCALE * zoomLevel;
    const scaledMarkerX = afterMarkerX * scale;
    const scaledMarkerY = afterMarkerY * scale;

    // Centered crop position
    const centeredCropX = Math.max(0, scaledMarkerX - viewport.containerWidth / 2);
    const centeredCropY = Math.max(0, scaledMarkerY - viewport.containerHeight / 2);

    // Actual crop position = centered - offset
    const actualCropX = centeredCropX - viewport.panX;
    const actualCropY = centeredCropY - viewport.panY;

    // Convert back to original coordinates
    const frontendPanX = actualCropX / scale;
    const frontendPanY = actualCropY / scale;

    // CRITICAL: Convert to backend's coordinate system (400×300 viewport)
    const OLD_VIEWPORT_WIDTH = 400;
    const OLD_VIEWPORT_HEIGHT = 300;
    const backendPanX = frontendPanX + (viewport.containerWidth / 2 - OLD_VIEWPORT_WIDTH / 2);
    const backendPanY = frontendPanY + (viewport.containerHeight / 2 - OLD_VIEWPORT_HEIGHT / 2);

    const updated = {
      ...annotation,
      viewport_after: {
        zoom: zoomLevel,
        panX: Math.round(backendPanX),
        panY: Math.round(backendPanY),
      },
    };
    onAnnotationChange(annotationIndex, updated);
  }, [annotation, annotationIndex, onAnnotationChange, zoomLevel]);

  // Separate refs to track last viewport for each side
  const lastAfterViewportRef = useRef<ViewportState | null>(null);
  const lastBeforeViewportRef = useRef<ViewportState | null>(null);

  // Handle marker drag - updates explicit marker position (preserves original before/after)
  const handleBeforeMarkerDrag = useCallback((pixelX: number, pixelY: number) => {
    const updated = {
      ...annotation,
      marker_before: { x: Math.round(pixelX), y: Math.round(pixelY) },
    };
    onAnnotationChange(annotationIndex, updated);
  }, [annotation, annotationIndex, onAnnotationChange]);

  const handleAfterMarkerDrag = useCallback((pixelX: number, pixelY: number) => {
    const updated = {
      ...annotation,
      marker_after: { x: Math.round(pixelX), y: Math.round(pixelY) },
    };
    onAnnotationChange(annotationIndex, updated);
  }, [annotation, annotationIndex, onAnnotationChange]);

  // Add marker mode handlers
  const handleEnterAddModeBefore = useCallback(() => {
    setAddModeImage(prev => prev === 'before' ? null : 'before');
  }, []);

  const handleEnterAddModeAfter = useCallback(() => {
    setAddModeImage(prev => prev === 'after' ? null : 'after');
  }, []);

  // Handle adding a marker to the before image
  // Note: We set `before` to the OTHER side's coordinates to keep the crop center stable,
  // and use `marker_before` for the actual clicked position. This prevents the image
  // from jumping when the marker is added.
  const handleAddBeforeMarker = useCallback((pixelX: number, pixelY: number) => {
    const markerCoords = { x: Math.round(pixelX), y: Math.round(pixelY) };
    // Use the after coordinates as the crop center (keeps image stable)
    const cropCenterCoords = annotation.after ? { x: annotation.after.x, y: annotation.after.y } : markerCoords;
    const updated = {
      ...annotation,
      before: cropCenterCoords,  // Crop center stays at other side's marker
      marker_before: markerCoords,  // Actual marker position where user clicked
    };
    onAnnotationChange(annotationIndex, updated);
    setAddModeImage(null); // Exit add mode after placing
  }, [annotation, annotationIndex, onAnnotationChange]);

  // Handle adding a marker to the after image
  const handleAddAfterMarker = useCallback((pixelX: number, pixelY: number) => {
    const markerCoords = { x: Math.round(pixelX), y: Math.round(pixelY) };
    // Use the before coordinates as the crop center (keeps image stable)
    const cropCenterCoords = annotation.before ? { x: annotation.before.x, y: annotation.before.y } : markerCoords;
    const updated = {
      ...annotation,
      after: cropCenterCoords,  // Crop center stays at other side's marker
      marker_after: markerCoords,  // Actual marker position where user clicked
    };
    onAnnotationChange(annotationIndex, updated);
    setAddModeImage(null); // Exit add mode after placing
  }, [annotation, annotationIndex, onAnnotationChange]);

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-white">

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Before image */}
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            Before
            {vBefore && <span className="text-xs text-gray-500 ml-1">({formatVersionDateAbbreviated(vBefore)})</span>}
            {!hasBefore && <span className="text-xs text-gray-400">(no marker)</span>}
          </div>
          <CroppedAnnotationImage
            key={`before-${annotationIndex}`}
            imageUrl={beforeImageUrl}
            cropCenterX={cropBeforeX}
            cropCenterY={cropBeforeY}
            markerX={markerBeforeX}
            markerY={markerBeforeY}
            side="before"
            num={annotation.num}
            zoomLevel={zoomLevel}
            onZoomChange={setZoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onMinZoomCalculated={handleMinZoomCalculated}
            showMarker={hasBefore && !annotation.hideMarker1}
            hasExplicitMarker={!!annotation.marker_before}
            onMarkerDrag={handleBeforeMarkerDrag}
            initialViewport={annotation.viewport_before}
            onViewportChange={handleBeforeViewportChange}
            onInteractionEnd={propagateBeforeViewport}
            isAddMode={addModeImage === 'before'}
            onAddMarker={handleAddBeforeMarker}
            canAddMarker={canAddBefore}
            onEnterAddMode={handleEnterAddModeBefore}
            hideMarker={annotation.hideMarker1}
            onHideMarkerChange={(hide) => {
              let updated = { ...annotation, hideMarker1: hide };
              // When unhiding, place marker at current viewport center
              if (!hide && beforeViewportRef.current) {
                const viewport = beforeViewportRef.current;

                // Calculate current viewport center in absolute coordinates
                // viewport center = marker - offset (offset is FROM marker position)
                const centerX = cropBeforeX - viewport.panX;
                const centerY = cropBeforeY - viewport.panY;

                // Place marker at viewport center
                updated.before = { x: Math.round(centerX), y: Math.round(centerY) };
                updated.marker_before = { x: Math.round(centerX), y: Math.round(centerY) };

                // Set viewport to center on new marker (offset will be 0)
                updated.viewport_before = {
                  zoom: zoomLevel,
                  panX: 0, // Centered on new marker (no offset)
                  panY: 0,
                };
              }
              onAnnotationChange(annotationIndex, updated);
            }}
          />
        </div>

        {/* After image */}
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500 lg:justify-end">
            <span className="w-2 h-2 rounded-full bg-green-400" />
            After
            {vAfter && <span className="text-xs text-gray-500 ml-1">({formatVersionDateAbbreviated(vAfter)})</span>}
            {!hasAfter && <span className="text-xs text-gray-400">(no marker)</span>}
          </div>
          <CroppedAnnotationImage
            key={`after-${annotationIndex}`}
            imageUrl={afterImageUrl}
            cropCenterX={cropAfterX}
            cropCenterY={cropAfterY}
            markerX={markerAfterX}
            markerY={markerAfterY}
            side="after"
            num={annotation.num}
            zoomLevel={zoomLevel}
            onZoomChange={setZoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onMinZoomCalculated={handleMinZoomCalculated}
            showMarker={hasAfter && !annotation.hideMarker2}
            hasExplicitMarker={!!annotation.marker_after}
            onMarkerDrag={handleAfterMarkerDrag}
            initialViewport={annotation.viewport_after}
            onViewportChange={handleAfterViewportChange}
            onInteractionEnd={propagateAfterViewport}
            isAddMode={addModeImage === 'after'}
            onAddMarker={handleAddAfterMarker}
            canAddMarker={canAddAfter}
            onEnterAddMode={handleEnterAddModeAfter}
            hideMarker={annotation.hideMarker2}
            onHideMarkerChange={(hide) => {
              let updated = { ...annotation, hideMarker2: hide };
              // When unhiding, place marker at current viewport center
              if (!hide && afterViewportRef.current) {
                const viewport = afterViewportRef.current;

                // Calculate current viewport center in absolute coordinates
                // viewport center = marker - offset (offset is FROM marker position)
                const centerX = cropAfterX - viewport.panX;
                const centerY = cropAfterY - viewport.panY;

                // Place marker at viewport center
                updated.after = { x: Math.round(centerX), y: Math.round(centerY) };
                updated.marker_after = { x: Math.round(centerX), y: Math.round(centerY) };

                // Set viewport to center on new marker (offset will be 0)
                updated.viewport_after = {
                  zoom: zoomLevel,
                  panX: 0, // Centered on new marker (no offset)
                  panY: 0,
                };
              }
              onAnnotationChange(annotationIndex, updated);
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Helper function to format version date (abbreviated)
const formatVersionDateAbbreviated = (version: string): string => {
  const dateMatch = version.match(/(\d{8})/);
  if (!dateMatch) return '';

  const dateStr = dateMatch[1];
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6)); // 1-indexed from API
  const day = parseInt(dateStr.substring(6, 8));   // 1-indexed from API

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Convert 1-indexed month to 0-indexed for array access
  return `${monthNames[month - 1]} ${day}, ${year}`;
};

// Main AnnotationEditor component
export function AnnotationEditor({
  annotationCoordinates,
  initialAnnotationCoordinates,
  primaryOriginalUrl,
  secondaryOriginalUrl,
  onChange,
  vBefore,
  vAfter,
}: AnnotationEditorProps) {
  // Use provided initial state or current annotationCoordinates as fallback
  const initialCoords = initialAnnotationCoordinates ?? annotationCoordinates;
  // Use cloudinary URLs if available, otherwise fall back to original URLs
  const beforeImageUrl = annotationCoordinates.cloudinary_before || primaryOriginalUrl;
  const afterImageUrl = annotationCoordinates.cloudinary_after || secondaryOriginalUrl;

  // Load image dimensions for coordinate conversion
  const [beforeImageSize, setBeforeImageSize] = useState<{ width: number; height: number } | null>(null);
  const [afterImageSize, setAfterImageSize] = useState<{ width: number; height: number } | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  useEffect(() => {
    if (beforeImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setBeforeImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        setImageError(`Failed to load before image`);
      };
      img.src = beforeImageUrl;
    }
  }, [beforeImageUrl]);

  useEffect(() => {
    if (afterImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setAfterImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        setImageError(`Failed to load after image`);
      };
      img.src = afterImageUrl;
    }
  }, [afterImageUrl]);

  const handleAnnotationChange = useCallback((index: number, updatedAnnotation: Annotation) => {
    const newCoords = { ...annotationCoordinates };
    newCoords.annotations = [...newCoords.annotations];
    newCoords.annotations[index] = updatedAnnotation;
    onChange(newCoords);
  }, [annotationCoordinates, onChange]);

  const handleAnnotationDelete = useCallback((index: number) => {
    const newCoords = { ...annotationCoordinates };
    newCoords.annotations = annotationCoordinates.annotations.filter((_, i) => i !== index);
    onChange(newCoords);
  }, [annotationCoordinates, onChange]);

  if (!annotationCoordinates.annotations || annotationCoordinates.annotations.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-gray-400 bg-gray-50">
        No annotations available
      </div>
    );
  }

  if (!beforeImageUrl || !afterImageUrl) {
    return (
      <div className="border rounded-lg p-8 text-center text-gray-400 bg-gray-50">
        <p>Missing image URLs</p>
        <p className="text-xs mt-2">Before: {beforeImageUrl || 'none'}</p>
        <p className="text-xs">After: {afterImageUrl || 'none'}</p>
      </div>
    );
  }

  if (imageError) {
    return (
      <div className="border rounded-lg p-8 text-center text-red-400 bg-red-50">
        <p>{imageError}</p>
        <p className="text-xs mt-2 break-all">Before URL: {beforeImageUrl}</p>
        <p className="text-xs break-all">After URL: {afterImageUrl}</p>
      </div>
    );
  }

  // Show loading state while image dimensions are being loaded
  if (!beforeImageSize || !afterImageSize) {
    return (
      <div className="border rounded-lg p-8 text-center text-gray-400 bg-gray-50">
        <div className="animate-pulse">Loading images...</div>
        <p className="text-xs mt-2">Before: {beforeImageSize ? '✓' : 'loading...'}</p>
        <p className="text-xs">After: {afterImageSize ? '✓' : 'loading...'}</p>
      </div>
    );
  }

  // Sort annotations by price: Free first, then by numeric price, Enterprise/Contact last
  const sortedAnnotations = [...annotationCoordinates.annotations].sort((a, b) => {
    const getPriceValue = (label: string): number => {
      const lowerLabel = label.toLowerCase().trim();

      // Free plans come first (including "Personal" which is often free)
      if (lowerLabel === 'free' || lowerLabel === 'personal' || lowerLabel.includes('free')) return 0;

      // Extract numeric price (e.g., $9, $15, $24)
      const priceMatch = label.match(/\$(\d+(?:\.\d+)?)/);
      if (priceMatch) {
        return parseFloat(priceMatch[1]);
      }

      // Common plan tier names with typical price order
      const tierOrder: Record<string, number> = {
        'starter': 5,
        'basic': 8,
        'pro': 9,
        'professional': 9,
        'team': 15,
        'plus': 18,
        'premium': 20,
        'business': 24,
        'enterprise': 999999,
      };

      // Check if label matches a known tier
      for (const [tier, value] of Object.entries(tierOrder)) {
        if (lowerLabel.includes(tier)) {
          return value;
        }
      }

      // Enterprise/Contact us comes last
      if (lowerLabel.includes('contact')) return 999999;

      // If no price or tier found, put in middle
      return 500000;
    };

    const aPrice = getPriceValue(a.label);
    const bPrice = getPriceValue(b.label);

    return aPrice - bPrice;
  });

  return (
    <div className="space-y-6">
      {sortedAnnotations.map((annotation, index) => (
        <AnnotationPairEditor
          key={`annotation-${annotation.num}-${index}`}
          annotation={annotation}
          annotationIndex={annotationCoordinates.annotations.findIndex(a => a.num === annotation.num)}
          beforeImageUrl={beforeImageUrl}
          afterImageUrl={afterImageUrl}
          beforeImageSize={beforeImageSize}
          afterImageSize={afterImageSize}
          onAnnotationChange={handleAnnotationChange}
          onAnnotationDelete={handleAnnotationDelete}
          initialAnnotation={initialCoords.annotations.find(a => a.num === annotation.num) || annotation}
          vBefore={vBefore}
          vAfter={vAfter}
        />
      ))}
    </div>
  );
}
